import { type EnvironmentProviders, LOCALE_ID, type Signal, inject, makeEnvironmentProviders, signal } from '@angular/core';

import { RT_KIT_LOCALE, type TRtKitLabelKey, type TRtKitTranslator, provideRtKitLabels } from '@rt-tools/ui-kit-v2';

// Подписи набора интерфейса на языке сайта. Набор везёт свои строки по-английски и спрашивает
// переводчика у приложения: часть подписей уходит в `aria`, и непереведённая читается вслух
// посреди русской страницы.
//
// Карта закрывает разговор и поля форм — то, что сайт из набора рисует. На остальные ключи
// переводчик отвечает пустым, и набор берёт свою английскую строку: так он сам это и
// задумал. Ключ, попавший на экран без перевода, найдётся глазами при первом же показе; пустая
// `aria`-подпись не нашлась бы вовсе.
const SITE_KIT_LABELS: Partial<Record<TRtKitLabelKey, string>> = {
    chatAttachAria: $localize`:@@kitChatAttachAria:Приложить файл`,
    chatAttachTooltip: $localize`:@@kitChatAttachTooltip:Приложить файл`,
    chatAttachmentFallback: $localize`:@@kitChatAttachmentFallback:Вложение`,
    chatCollapse: $localize`:@@kitChatCollapse:Свернуть разговор`,
    chatComposerAria: $localize`:@@kitChatComposerAria:Поле сообщения`,
    chatDeleteAria: $localize`:@@kitChatDeleteAria:Удалить сообщение`,
    chatDeleteTooltip: $localize`:@@kitChatDeleteTooltip:Удалить`,
    chatDropOverlay: $localize`:@@kitChatDropOverlay:Отпустите файлы, чтобы приложить их к сообщению`,
    chatEmptyHint: $localize`:@@kitChatEmptyHint:Разговор пока недоступен.`,
    chatEmptyThread: $localize`:@@kitChatEmptyThread:Сообщений пока нет.`,
    chatExpand: $localize`:@@kitChatExpand:Развернуть разговор на весь экран`,
    chatMessageDeleted: $localize`:@@kitChatMessageDeleted:Сообщение удалено`,
    chatPlaceholder: $localize`:@@kitChatPlaceholder:Напишите сообщение`,
    chatRefreshAria: $localize`:@@kitChatRefreshAria:Обновить сообщения`,
    chatRefreshTooltip: $localize`:@@kitChatRefreshTooltip:Обновить сообщения`,
    chatRetryAria: $localize`:@@kitChatRetryAria:Отправить ещё раз`,
    chatRetryTooltip: $localize`:@@kitChatRetryTooltip:Отправить ещё раз`,
    chatSendAria: $localize`:@@kitChatSendAria:Отправить сообщение`,
    chatSendLabel: $localize`:@@kitChatSendLabel:Отправить`,
    chatStatusFailed: $localize`:@@kitChatStatusFailed:Не отправлено`,
    chatStatusRead: $localize`:@@kitChatStatusRead:Прочитано`,
    chatStatusSending: $localize`:@@kitChatStatusSending:Отправляется`,
    chatStatusSent: $localize`:@@kitChatStatusSent:Доставлено`,
    chatTitle: $localize`:@@kitChatTitle:Разговор`,
    fieldErrorEmail: $localize`:@@kitFieldErrorEmail:Неверный адрес почты`,
    fieldErrorMaxLength: $localize`:@@kitFieldErrorMaxLength:Слишком длинно`,
    fieldErrorMinLength: $localize`:@@kitFieldErrorMinLength:Слишком коротко`,
    fieldErrorPattern: $localize`:@@kitFieldErrorPattern:Неверный формат`,
    fieldErrorRequired: $localize`:@@kitFieldErrorRequired:Обязательное поле`,
    uiClear: $localize`:@@kitUiClear:Очистить`,
    uiClose: $localize`:@@kitUiClose:Закрыть`,
};

// Переводчик, которого набор спрашивает о подписи. Пустая строка означает «ответа нет», и набор
// берёт английское умолчание.
export const translateKitLabel: TRtKitTranslator = (key: TRtKitLabelKey): string => SITE_KIT_LABELS[key] ?? '';

// Подписи и локаль набора для сайта. Локаль объявляется отдельным поставщиком, а не доводом
// `provideRtKitLabels`: сборка сайта одна на четыре языка, и язык известен только внедрением —
// `LOCALE_ID`. Без него набор форматирует времена сообщений по-английски.
export function provideSiteKitLabels(): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideRtKitLabels({ translator: signal(translateKitLabel).asReadonly() }),
        {
            provide: RT_KIT_LOCALE,
            useFactory: (): Signal<string> => signal(inject(LOCALE_ID)).asReadonly(),
        },
    ]);
}
